import { type IChartApi, type ISeriesApi, createChart, ColorType, LineSeries } from "lightweight-charts";
import { Panel } from "./panel-base.ts";
import { el } from "../utils/dom.ts";
import { getIndicatorsWeekly, type WeeklyIndicator } from "../services/api-client.ts";

const MIN_POINTS = 3; // below this the series is noise — show "accumulating" instead.

// Weekly sentiment average per domain (the "sentiment as indicator" view).
// Reads indicators_weekly.sentiment_avg. This data accumulates slowly (only
// news with a sentiment score contributes), so it shows an explicit
// "accumulating" state until enough weeks exist — distinct from the daily
// SentimentTrendPanel which draws the raw 30-day series.
export class SentimentWeeklyChart extends Panel {
  private chart: IChartApi | null = null;
  private line: ISeriesApi<"Line"> | null = null;
  private readonly chartHost: HTMLElement;
  private domain: string;

  constructor(domain = "social") {
    super({ id: `sentiment-weekly-${domain}`, title: "Sắc thái theo tuần", subtitle: "Chỉ số tuần theo lĩnh vực" });
    this.domain = domain;
    this.chartHost = el("div", { className: "chart-host" });
    this.setContent(this.chartHost);
  }

  async load(): Promise<void> {
    try {
      const res = await getIndicatorsWeekly(`?domain=${this.domain}&province=national&weeks=12`);
      const points = res.items
        .filter((r: WeeklyIndicator) => r.sentiment_avg !== null)
        .sort((a, b) => a.week.localeCompare(b.week))
        .map((r) => ({ time: weekTime(r.week), value: r.sentiment_avg as number }));

      if (points.length < MIN_POINTS) {
        this.setContent(
          el("div", { className: "panel-empty" }, [
            "Chỉ số sắc thái theo tuần đang tích lũy dữ liệu.",
          ]),
        );
        return;
      }
      const chart = this.ensureChart();
      this.line?.setData(points);
      chart.timeScale().fitContent();
    } catch {
      this.setError();
    }
  }

  private ensureChart(): IChartApi {
    if (this.chart) return this.chart;
    this.chart = createChart(this.chartHost, {
      height: 200,
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: "#9aa4b2",
        attributionLogo: false,
      },
      grid: { vertLines: { visible: false }, horzLines: { color: "#1e2733" } },
      rightPriceScale: { borderVisible: false },
      timeScale: { borderVisible: false },
      autoSize: true,
    });
    this.line = this.chart.addSeries(LineSeries, { color: "#7cc4ff", lineWidth: 2 });
    return this.chart;
  }
}

// ISO week 'YYYY-Www' -> the Monday of that week as a lightweight-charts date.
function weekTime(week: string): never {
  const m = /^(\d{4})-W(\d{2})$/.exec(week);
  if (!m) return `${week}-01` as never;
  const year = Number(m[1]);
  const wk = Number(m[2]);
  // ISO week 1 contains Jan 4; Monday of week N = Jan4 - (its weekday-1) + (N-1)*7.
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Dow = (jan4.getUTCDay() + 6) % 7; // Mon=0
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - jan4Dow + (wk - 1) * 7);
  return monday.toISOString().slice(0, 10) as never;
}
